import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Tractor, Package, CheckCircle2, XCircle, Loader2, Mail, MapPin, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const decisionPrompt = (label) => {
  const notes = window.prompt(`${label} — optional notes to the user:`, '');
  return notes;   // string or null (cancelled)
};

const AdminFarmers = () => {
  const [tab, setTab] = useState('farmers');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [farmers, setFarmers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'farmers') {
        const r = await axios.get(`${API}/admin/farmers?${statusFilter ? 'status=' + statusFilter : ''}`);
        setFarmers(r.data.farmers || []);
      } else {
        const r = await axios.get(`${API}/admin/farmer-commodities?${statusFilter ? 'status=' + statusFilter : ''}`);
        setCommodities(r.data.commodities || []);
      }
    } catch (err) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (kind, id, decision) => {
    const notes = decisionPrompt(decision === 'approved' ? 'Approve' : 'Reject');
    if (notes === null) return;
    setBusy(id);
    try {
      const base = kind === 'farmers'
        ? `${API}/admin/farmers/${id}/${decision === 'approved' ? 'approve' : 'reject'}`
        : `${API}/admin/farmer-commodities/${id}/${decision === 'approved' ? 'approve' : 'reject'}`;
      await axios.post(base, { notes });
      toast.success(`Marked ${decision}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-farmers-page">
      <div className="flex items-center gap-3 mb-6">
        <Tractor className="h-7 w-7 text-green-600" />
        <div>
          <h1 className="text-2xl font-bold">Farmers & Market</h1>
          <p className="text-sm text-gray-500">Approve farmer applications and moderate commodity listings.</p>
        </div>
      </div>

      <div className="flex gap-1 border-b mb-4 overflow-x-auto">
        {[
          { id: 'farmers', label: 'Farmer applications', icon: Tractor },
          { id: 'commodities', label: 'Commodity listings', icon: Package },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" data-testid="admin-status-filter">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      {loading ? (
        <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>
      ) : tab === 'farmers' ? (
        farmers.length === 0 ? <p className="text-center text-sm text-gray-500 p-8">No farmer applications.</p> : (
          <div className="space-y-3">
            {farmers.map(f => (
              <Card key={f._id} data-testid={`farmer-row-${f._id}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="font-semibold">{f.farm_name}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                      <Mail className="h-3 w-3" />{f.email} · <MapPin className="h-3 w-3" />{f.location}
                    </p>
                    <p className="text-xs text-gray-500">
                      {f.years_experience} yrs · {f.farm_size_hectares} ha · {(f.produce_categories || []).join(', ')}
                    </p>
                    {f.bio && <p className="text-xs text-gray-600 mt-1 italic">{f.bio}</p>}
                    <Badge className={`${STATUS_COLORS[f.status]} border-none mt-2`}>{f.status}</Badge>
                  </div>
                  {f.status === 'pending' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => decide('farmers', f._id, 'approved')} disabled={busy === f._id} data-testid={`approve-farmer-${f._id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => decide('farmers', f._id, 'rejected')} disabled={busy === f._id} data-testid={`reject-farmer-${f._id}`}>
                        <XCircle className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        commodities.length === 0 ? <p className="text-center text-sm text-gray-500 p-8">No commodity listings.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {commodities.map(c => (
              <Card key={c._id} data-testid={`commodity-row-${c._id}`}>
                <CardContent className="p-4 flex gap-3">
                  {c.photo_url && <img src={`${BACKEND_URL}${c.photo_url}`} alt={c.name} className="w-20 h-20 rounded object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{c.name}</p>
                    <p className="text-xs text-gray-500">from {c.farm_name} · {c.farmer_email}</p>
                    <p className="text-sm font-medium mt-1">₦{Number(c.price).toLocaleString()}/{c.unit} · {c.quantity_available} available</p>
                    <p className="text-xs text-gray-500">{c.category} · {c.pickup_location}</p>
                    <Badge className={`${STATUS_COLORS[c.status]} border-none mt-2`}>{c.status}</Badge>
                    {c.status === 'pending' && (
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => decide('commodities', c._id, 'approved')} disabled={busy === c._id} data-testid={`approve-commodity-${c._id}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => decide('commodities', c._id, 'rejected')} disabled={busy === c._id} data-testid={`reject-commodity-${c._id}`}>Reject</Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default AdminFarmers;
